import React from 'react';
import {Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignProps} from '@/types/campaign';
import BriefField from '../../shared/BriefField';
import BriefSection from '../../shared/BriefSection';
import DividedStack from '../../shared/DividedStack';
import resolveCompensationType from '../../utils/resolveCompensationType';
import styles from './CreatorPayments.module.scss';

interface CreatorPaymentsProps {
  campaign: CampaignProps;
}

function CreatorPayments({campaign}: CreatorPaymentsProps) {
  const {
    suggestedCostForCreationsMin,
    suggestedCostForCreations,
    fixedCostForCreations,
    deliverablesType,
    estimatedFulfillmentValue,
  } = campaign;

  const creatorCompensationType = resolveCompensationType(campaign);

  const hasRangeCompensation =
    creatorCompensationType === 'range' &&
    (suggestedCostForCreationsMin != null || suggestedCostForCreations != null);
  const hasFixedCompensation =
    creatorCompensationType === 'fixed' && fixedCostForCreations != null;
  const hasProductDetails =
    deliverablesType != null && estimatedFulfillmentValue != null;

  if (!hasRangeCompensation && !hasFixedCompensation && !hasProductDetails) {
    return null;
  }

  const rangeRow = hasRangeCompensation ? (
    <div className={styles.row}>
      {suggestedCostForCreationsMin != null ? (
        <div className={styles.col}>
          <BriefField label={translate('Minimum compensation')}>
            <Body size="xl">
              {suggestedCostForCreationsMin.toLocaleString()} USD
            </Body>
          </BriefField>
        </div>
      ) : null}
      {suggestedCostForCreations != null ? (
        <div className={styles.col}>
          <BriefField label={translate('Maximum compensation')}>
            <Body size="xl">
              {suggestedCostForCreations.toLocaleString()} USD
            </Body>
          </BriefField>
        </div>
      ) : null}
    </div>
  ) : null;

  const fixedRow = hasFixedCompensation ? (
    <BriefField label={translate('Fixed compensation')}>
      <Body size="xl">
        {(fixedCostForCreations as number).toLocaleString()} USD
      </Body>
    </BriefField>
  ) : null;

  const productTypeRow = hasProductDetails ? (
    <BriefField
      label={
        deliverablesType === 'gift_codes'
          ? translate('Discount code')
          : translate('Ship product')
      }
    >
      <Body size="md">
        {deliverablesType === 'gift_codes'
          ? translate(
              'Creators will be asked to purchase a product using the code provided'
            )
          : translate(
              'Creators will receive a product to feature in their content'
            )}
      </Body>
    </BriefField>
  ) : null;

  const productValueRow = hasProductDetails ? (
    <BriefField label={translate('Product cash value')}>
      <Body size="xl">
        {(estimatedFulfillmentValue as number).toLocaleString()} USD
      </Body>
    </BriefField>
  ) : null;

  return (
    <BriefSection title={translate('Creator payments')}>
      <DividedStack
        items={[rangeRow, fixedRow, productTypeRow, productValueRow]}
      />
    </BriefSection>
  );
}

export default CreatorPayments;
